import { kebabCase, minBy, isEmpty } from 'lodash';

import { LocaleService } from '@sm360/widget-commons-core-services';
import { ModelYear } from '@sm360/widget-commons-vehicle';
import { CatalogModelSummary, EnhancedPricing, Incentives, Price, PurchaseTerm } from '@sm360/widget-commons-tiles';

const { EXTERIOR_COLOUR_STATUSES } = ModelYear;

function findTermWithLowestPayment(terms) {
    return minBy(terms, (term) => term.calculatedPayment);
}

function mapBestIncentives(model) {
    const total = model.highRebate || model.rebate || 0;
    const modelElectricRebate = model.purchaseMethods.getElectricRebate();
    const electricRebateIncluded =
        !isEmpty(modelElectricRebate) && !!modelElectricRebate.rebate && !Number.isNaN(modelElectricRebate.rebate);

    return new Incentives({
        total,
        electricRebateIncluded,
    });
}

function mapPurchaseTerm(purchaseInfo, taxCodes) {
    const term = findTermWithLowestPayment(purchaseInfo.terms);

    return new PurchaseTerm({
        payment: term.calculatedPayment,
        paymentFreq: term.paymentFrequency,
        cashdown: term.cashDown,
        rate: term.apr,
        creditRate: term.creditRate,
        term: term.term,
        incentives: [],
        taxesDetails: {
            display: true,
            paymentTaxes: term.paymentTaxes,
            taxesDetail: `(${taxCodes.join('+')})`,
            totalPayment: term.calculatedPayment + term.paymentTaxes,
        },
    });
}

function mapMultimedia(model) {
    let picDisclaimer;
    if (!model.exteriorColours) {
        return null;
    }

    if (model.pictureDisclaimer) {
        picDisclaimer = model.pictureDisclaimer;
    } else if (
        model.exteriorColourStatus < EXTERIOR_COLOUR_STATUSES.FULL_COLORIZED &&
        model.exteriorColourStatus > EXTERIOR_COLOUR_STATUSES.NO_IMAGE
    ) {
        picDisclaimer = LocaleService.t('MISSING_PHOTO.REFERENCE_PHOTO');
    } else {
        picDisclaimer = '';
    }

    return {
        pictureDisclaimer: picDisclaimer,
        exterior: {
            side: model.getIn(['exteriorColours', 'photo1']),
        },
    };
}

function mapDisclaimers(model) {
    if (!model.purchaseMethods) {
        return null;
    }

    return model.purchaseMethods.getDisclaimersForCurrentMethod();
}

function mapCashEnhancedPricing(model) {
    if (!model.purchaseMethods || !model.purchaseMethods.cashPurchase) {
        return null;
    }

    return {
        cashSalePrice:
            model.purchaseMethods.cashPurchase.pricingAmounts.msrp -
            model.purchaseMethods.cashPurchase.getTotalRebates(),
    };
}

function mapVehiclePrice(model) {
    if (!model.purchaseMethods || !model.purchaseMethods.cashPurchase) {
        return null;
    }

    const msrp =
        model.purchaseMethods.cashPurchase.pricingAmounts.msrp - model.purchaseMethods.cashPurchase.getTotalRebates();
    const { taxesAndDuties, freightPDI } = model.purchaseMethods.cashPurchase.pricingAmounts;
    const total = msrp + taxesAndDuties + freightPDI;

    return new Price({
        msrp,
        freightPDI,
        taxesAndDuties,
        total,
        shortDisclaimer: '',
    });
}

function mapVehicleEnhancedPricing(model) {
    return new EnhancedPricing({
        bestIncentives: mapBestIncentives(model),
        cash: mapCashEnhancedPricing(model),
        lease: mapPurchaseTerm(model.purchaseMethods.lease, model.purchaseMethods.taxCodes),
        finance: mapPurchaseTerm(model.purchaseMethods.finance, model.purchaseMethods.taxCodes),
    });
}

function mapVehicleCostOfCredit(model) {
    const costOfCredit = model.purchaseMethods.getSelectedFinanceCostOfCredit();
    return costOfCredit;
}

export default {
    mapToCatalogModelSummary(model) {
        const summary = {
            bodyStyle: {
                slug: model.bodyStyleCodeOriginal,
                name: model.bodyStyle,
            },
            make: {
                slug: kebabCase(model.make),
                name: model.make,
            },
            model: {
                id: null,
                slug: kebabCase(model.model),
                name: model.model,
            },
            multimedia: mapMultimedia(model),
            disclaimer: mapDisclaimers(model),
            price: mapVehiclePrice(model),
            enhancedPricing: mapVehicleEnhancedPricing(model),
            costOfCredit: mapVehicleCostOfCredit(model),
        };

        return new CatalogModelSummary(summary);
    },
};
